import React, { useCallback, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { serverMessage } from '../../../../shared/error/server-message'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { MenuItem } from '../../../../shared/menu/menu'
import { IconAdd, IconList } from '../../../../shared/icon/icon'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { useRoleSysPackages } from '../../../../role/hook/role-sys'
import { listPackages } from './packages-api'
import { AddPackageDialog } from './add-package-dialog'
import { PackagesUnavailable, useAvailable } from './unavailable'

const DASH = '—'
const CHANNELS = ['unity', 'unity-dev']

// One row per package, with the two channels' figures flattened.
const rowOf = p => {
  const ch = p.channels || {}
  const unity = ch.unity || {}
  const dev = ch['unity-dev'] || {}
  return {
    name: p.name,
    displayName: p.displayName,
    released: unity.latest || DASH,
    prerelease: dev.latest || DASH,
    versions: CHANNELS.reduce((sum, c) => sum + ((ch[c] && ch[c].versions) || 0), 0),
    downloads30d: CHANNELS.reduce((sum, c) => sum + ((ch[c] && ch[c].downloads30d) || 0), 0),
    liveKeys: p.liveKeys > 0 ? 'Live' : 'None'
  }
}

const PackagesListPage = () => {
  const role = useRoleSysPackages()
  const history = useHistory()
  const [error, failure, setError] = useErrorLine()
  const [adding, setAdding] = useState(false)
  const tableRef = useRef()
  const cache = useRef([])

  // The grid asks for the first block on load, search and reload: that is when
  // the packages are fetched again, so its own overlay and reload work. The
  // datasource keeps the first function it is given, which is why this one has
  // no changing dependencies.
  const getRows = useCallback(async (start, end, search) => {
    if (start === 0) {
      try {
        const v = await listPackages()
        setError(null)
        cache.current = (v || []).map(rowOf)
      } catch (err) {
        setError(serverMessage(err, "Packages couldn't be loaded."), err)
        return { data: [], count: 0 }
      }
    }
    const q = (search || '').trim().toLowerCase()
    const all = q ? cache.current.filter(r => `${r.name} ${r.displayName || ''}`.toLowerCase().includes(q)) : cache.current
    return { data: all.slice(start, end), count: all.length }
  }, [setError])
  // The card's menu (owner 2026-10-29): Add package pinned, kept on a phone.
  const menu = <MenuItem key="add" Icon={IconAdd} text="Add package" pin keepOnMobile show={!!role.accessCreate} onClick={() => setAdding(true)} />
  // A package with nothing released opens on the channel that has versions.
  const open = r => history.push(`/admin/packages/${encodeURIComponent(r.name)}${r.released === DASH ? '?channel=unity-dev' : ''}`)

  return (
    <FleetPage title="Packages" purpose="Unity packages EducationXR serves to the Unity Package Manager.">
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {!error && <FleetGrid title="packages" tableRef={tableRef} menu={menu}>
        <TableAgInfinite ref={tableRef} queryKey="" onGetRows={getRows}
          onRowClicked={open}
          emptyState={{ Icon: IconList, title: 'No packages', message: 'Add a package to start publishing versions.' }}>
          <ColumnFormat field="name" header="Package" sortable={false} minWidth={220} flex={1.4}
            render={r => (r.data ? <span className="d-flex flex-column"><span>{r.value}</span>{r.data.displayName && <span className="sw-cell-sub">{r.data.displayName}</span>}</span> : '')} />
          <ColumnFormat field="released" header="Released" sortable={false} minWidth={120} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="prerelease" header="Pre-release" sortable={false} minWidth={130} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="versions" header="Versions" sortable={false} minWidth={100} render={r => (r.data ? String(r.value) : '')} />
          <ColumnFormat field="downloads30d" header="Downloads 30 d" sortable={false} minWidth={130} render={r => (r.data ? String(r.value) : '')} />
          <ColumnFormat field="liveKeys" header="Publish key" sortable={false} minWidth={110} render={r => (r.data ? r.value : '')} />
        </TableAgInfinite>
      </FleetGrid>}
      <AddPackageDialog open={adding} onCancel={() => setAdding(false)} onAdded={name => { setAdding(false); history.push(`/admin/packages/${encodeURIComponent(name)}`) }} />
    </FleetPage>
  )
}

export const PackagesList = () => (useAvailable() ? <PackagesListPage /> : <PackagesUnavailable />)
